import { LOCATION_INITIALIZED } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injector, isDevMode, provideAppInitializer } from '@angular/core';
import { LocalStorageService, NestechHttpLoaderFactory } from '@nestech/utils';
import {
  provideTranslateLoader,
  provideTranslateService,
  TranslateService,
  TranslationObject,
} from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { catchError, Observable, of, timeout } from 'rxjs';
import { AvailableLanguage } from '../environment/environment.model';
import { EnvironmentService } from '../environment/environment.service';
import { LanguageService } from '../util/language.service';

export const I18NFetchModes = ['disabled', 'cache', 'noFetch'] as const;

export type I18NFetchMode = (typeof I18NFetchModes)[number];
export const I18NFetchModeArray: I18NFetchMode[] = [...I18NFetchModes];

export function initializeLocaleFactory(
  translate: TranslateService,
  languageService: LanguageService,
  injector: Injector,
  environmentService: EnvironmentService,
) {
  return async () => {
    await injector.get(LOCATION_INITIALIZED, Promise.resolve(null));

    const currentLang = languageService.currentLanguage();
    translate.setFallbackLang(environmentService.environment.defaultLanguage); // This language will be used as a fallback when a translation isn't found in the current language
    // Not awaited: the translate pipe re-renders once the file arrives, and the member calls must not wait for it.
    languageService.changeLanguage(currentLang as AvailableLanguage);
  };
}

export const AppInitializerTranslate = provideAppInitializer(() => {
  const initializerFn = initializeLocaleFactory(
    inject(TranslateService),
    inject(LanguageService),
    inject(Injector),
    inject(EnvironmentService),
  );
  return initializerFn();
});

export const TranslateProvider = provideTranslateService({
  loader: provideTranslateLoader(() => {
    const httpClient = inject(HttpClient);
    const prefix = 'i18n/';
    const url = '/api/Translation';
    const mode = new LocalStorageService().getItem('liveI18NMode');

    const devBust = DateTime.now().toMillis(); // dev: new value every time
    const prodBust = DateTime.now().toMillis().toString().slice(0, -7); // prod: 10-min granularity

    const isDevelopment = isDevMode();
    const bust = isDevelopment ? devBust : prodBust;

    const getTranslationFn = (language: string): Observable<TranslationObject> => {
      const path = `${prefix}${language}.json?${bust}`;
      switch (mode) {
        case 'noFetch':
          return of({});
        case 'disabled':
          return httpClient.get<TranslationObject>(path);
        default:
          return httpClient.get<TranslationObject>(`${url}/${language}`).pipe(
            timeout({
              each: 1000,
              with: () => httpClient.get<TranslationObject>(path),
            }),
            catchError(() => httpClient.get<TranslationObject>(path)),
          );
      }
    };
    return NestechHttpLoaderFactory(getTranslationFn);
  }),
});
